import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
  FormControlLabel,
  FormLabel,
  FormControl,
  RadioGroup,
  Radio,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "../../../../../../components/Input";
import { useUserStore } from "../../../../../../store/useUserStore";
import { useBankNames } from "../../../../../../hooks/useBankNames";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import FileUpload from "../../../../../../components/FileUpload";
import { getDocument } from "../../../../../../services/dashboard";

export default function BankingInfoForm({
  bankingInfo,
  iceClaimNumber,
  onBankingCommentChange,
  documentData: initialDocumentData,
  isAssigned,
  claimStatus,
  bankingComment,
  commentData,
  isEditable,
  handleBankingFieldChange,
}: any) {
  const { bankOptions } = useBankNames();
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [documents, setDocuments] = useState<any>({});
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});

  const role = useUserStore((state) => state.role);
  const addDocument = useClaimFormStore((state) => state.addDocument);
  const documentsFromStore = useClaimFormStore((state) => state.documents);
  const clearDocuments = useClaimFormStore((s) => s.clearDocuments);

  useEffect(() => {
  // Always start from backend documents
  const grouped = initialDocumentData.reduce(
    (acc: Record<string, any[]>, doc: any) => {
      if (!acc[doc.document_type]) acc[doc.document_type] = [];
      acc[doc.document_type].push(doc);
      return acc;
    },
    {}
  );

  const bankDocTypes = ["BANK_STATEMENT", "SIGNATURE_OF_CLAIMANT"];

  // Only merge local drafts when editing
  if (isEditable) {
    Object.entries(documentsFromStore).forEach(([type, file]) => {
      if (bankDocTypes.includes(type)) {
        grouped[type] = [
          {
            file_name: file.name,
            file_url: URL.createObjectURL(file),
            isLocal: true,
          },
        ];
      }
    });
  }

  setDocuments(grouped);
}, [initialDocumentData, documentsFromStore, isEditable]);


  const handleDeleteDocument = (type: string, index: number) => {
    setDocuments((prev: any) => {
      const updated = [...(prev[type] || [])];
      updated.splice(index, 1);
      return { ...prev, [type]: updated };
    });
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };
  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "BANKING_INFORMATION"
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc.document_id);
        if (!res || res.status !== 200) {
          throw new Error("Failed to fetch document");
        }

        const blob = res.data; // Axios already gives blob here
        const url = URL.createObjectURL(blob);

        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  useEffect(() => {
  if (!isEditable) {
    clearDocuments(); // clears drafts when exiting edit mode
  }
}, [isEditable]);

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={bankingInfo?.bank_name || ""}
          height={40}
          width="100%"
          label="Name of bank"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingFieldChange("bank_name", e.target.value)
          }
        >
          {(bankOptions.includes(bankingInfo?.bank_name)
            ? bankOptions
            : [...bankOptions, bankingInfo?.bank_name]
          ).map((bank) => (
            <MenuItem key={bank} value={bank}>
              {bank}
            </MenuItem>
          ))}
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfo.account_number}
          height={40}
          width="100%"
          label="Account number"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingFieldChange("account_number", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfo.branch_code}
          height={40}
          width="100%"
          label="Branch code"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingFieldChange("branch_code", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={bankingInfo.account_type || ""}
          fullWidth
          label="Account type"
          variant="outlined"
          onChange={(e: any) =>
            handleBankingFieldChange("account_type", e.target.value)
          }
          width="100%"
          height={40}
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
        >
          <MenuItem disabled value="" sx={{ background: "background.default" }}>
            -Account Type-
          </MenuItem>
          <MenuItem
            value="cheque account"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Cheque Account
          </MenuItem>
          <MenuItem
            value="transmission account"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Transmission Account
          </MenuItem>
          <MenuItem
            value="savings account"
            sx={{ background: "background.default" }}
          >
            Savings Account
          </MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfo.account_name}
          height={40}
          width="100%"
          label="Account Name"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingFieldChange("account_name", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfo.cell_phone_number}
          height={40}
          width="100%"
          label="Contact Number"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingFieldChange("cell_phone_number", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel
            component="legend"
            sx={{
              fontWeight: "bold",
              color: "text.primary",
              "&.Mui-focused": {
                color: "text.primary",
              },
            }}
          >
            Verified
          </FormLabel>
          <RadioGroup
            row
            name="is_verified"
            value={bankingInfo.is_verified.toString()}
            onChange={(e) =>
              handleBankingFieldChange("is_verified", e.target.value)
            }
          >
            <FormControlLabel
              value="true"
              control={<Radio disabled={!(role === "consultant")} />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio disabled={!(role === "consultant")} />}
              label="No"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid size={12}>
        <Grid container rowSpacing={3} columnSpacing={10}>
          {Object.entries(documents).map(([type, docs]: any) => (
            <Grid size={{ xs: 12, md: 4 }}>
              <Box key={type} mt={2}>
                <Typography fontWeight={600} textAlign="left" fontSize={18}>
                  {type
                    .replace(/_/g, " ")
                    .toLocaleLowerCase()
                    .replace(/^\w/, (c: any) => c.toUpperCase())}
                </Typography>
                {docs.map((doc: any, index: any) => (
                  <Box
                    key={index}
                    sx={{
                      background: "#F5EEE9",
                      color: "#FF0000",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexGrow={1}
                    >
                      <PictureAsPdfIcon />
                      <Box>
                        <Typography fontSize={14}>{doc.file_name}</Typography>
                      </Box>
                    </Box>
                    {isEditable ? (
                      <IconButton
                        onClick={() => handleDeleteDocument(type, index)}
                        size="small"
                      >
                        <DeleteIcon sx={{ color: "#FF0000" }} />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => handlePreview(doc)}
                        size="small"
                      >
                        <VisibilityIcon sx={{ color: "#EC6E00" }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {isEditable && docs.length === 0 && (
                  <Box mt={1}>
                    <FileUpload
                      inputId={`upload-${type}`}
                      onChange={(e: any) => {
                        if (e.target.files.length > 0) {
                          const file = e.target.files[0];
                           if (file.size > 2 * 1024 * 1024) {
                            setFileErrors(prev => ({ ...prev, [type]: "File must be less than 2MB" }));
                            return;
                          }
                          setFileErrors(prev => ({ ...prev, [type]: "" }));
                          const newDoc = {
                            file_name: file.name,
                            file_url: URL.createObjectURL(file),
                            isLocal: true,
                          };
                          setDocuments((prev: any) => ({
                            ...prev,
                            [type]: [newDoc],
                          }));
                          addDocument(type, file);
                        }
                      }}
                    />
                    {fileErrors && (
                      <Typography color="error" fontSize={14}>
                       {fileErrors[type]}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      {comments?.length > 0 && (
        <Grid size={12} mt={4}>
          {comments.map((comment: any, index: number) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#F7F7F7",
                boxShadow: "none",
                borderRadius: "6px",
                mb: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {comment.commentedByName}{" "}
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="#EC6E00"
                    >
                      - {comment.role_at_time_of_comment?.toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {new Date(comment.created_on).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    | {formatDate(comment.created_on)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={15} color="#333" lineHeight={1.6}>
                  {comment.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      )}

      {isAssigned && claimStatus !== "PAID" && (
        <Grid size={{ md: 12 }} mt={3}>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            sx={{ display: "block", textAlign: "left" }}
          >
            Comment
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            disabled={isEditable}
            value={bankingComment}
            onChange={onBankingCommentChange}
          />
        </Grid>
      )}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
